// (c) chaehongee. Confidential and proprietary.

import { useState, useCallback, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Plus, Copy } from 'lucide-react'
import styles from './SessionTabs.module.css'
import IconButton from '../IconButton/IconButton'
import { useAppDispatch, useAppSelector } from '../../store/hooks/hooks'
import { closeTab, openTab, openTabAfter, setActiveTab, markSessionFresh } from '../../store/slices/sessionTabsSlice'
import { createSession, duplicateSession, getSessionInfo, updateSession } from '../../services/session'
import { useAuth } from '../../store/hooks/useAuth'
import {
    updateSessionTitle,
    upsertSession,
    setSessionError,
    setSessionLoading,
    setSessionLoadingMessage,
} from '../../store/slices/sessionSlice'
import { setPendingInitialSelection } from '../../store/slices/selectionSlice'
import { cancelSessionStream } from '../../services/sessionStreams/SessionStreamRegistry'
import { removeChatState } from '../../store/slices/chatSlice'
import { removeCanvasState } from '../../store/slices/canvasSlice'
import { removeSelectionState } from '../../store/slices/selectionSlice'
import { EditableTitle } from './EditableTitle'
import { ContextMenu } from '../Canvas/features/contextMenu/ContextMenu'
import type { MenuAnchor, ContextMenuItem } from '../Canvas/features/contextMenu/types'

export function SessionTabs(): ReactElement | null {
    const dispatch = useAppDispatch()
    const { user } = useAuth()
    const { t } = useTranslation('common')
    const openSessionIds = useAppSelector((s) => s.sessionTabs.openSessionIds)
    const activeSessionId = useAppSelector((s) => s.sessionTabs.activeSessionId)
    const unreadById = useAppSelector((s) => s.sessionTabs.unreadCountBySessionId)
    const sessionsById = useAppSelector((s) => s.session.sessionsById)
    const chatBySessionId = useAppSelector((s) => s.chat.bySessionId)
    const [ctxMenu, setCtxMenu] = useState<{ open: boolean; anchor: MenuAnchor | null; sessionId: string | null }>({ open: false, anchor: null, sessionId: null })

    if (openSessionIds.length === 0) return null

    const handleActivate = (sessionId: string) => {
        dispatch(setActiveTab(sessionId))
        try { localStorage.setItem('session_id', sessionId) } catch { }
        try {
            window.history.pushState({}, '', `/session/${sessionId}`)
            window.dispatchEvent(new PopStateEvent('popstate'))
        } catch { }
    }

    const handleClose = (sessionId: string) => {
        const isActive = sessionId === activeSessionId
        cancelSessionStream(sessionId)
        dispatch(removeChatState({ sessionId }))
        dispatch(removeCanvasState({ sessionId }))
        dispatch(removeSelectionState({ sessionId }))
        dispatch(closeTab(sessionId))
        if (isActive) {
            const nextActive = (() => {
                const remaining = openSessionIds.filter((id) => id !== sessionId)
                return remaining[0] ?? null
            })()
            try {
                if (nextActive) {
                    window.history.pushState({}, '', `/session/${nextActive}`)
                } else {
                    window.history.replaceState({}, '', '/sessions')
                }
                window.dispatchEvent(new PopStateEvent('popstate'))
            } catch { }
        }
    }

    const handleNewTab = async () => {
        if (!user) {
            window.dispatchEvent(new CustomEvent('show:login', { detail: { returnUrl: window.location.pathname } }))
            return
        }

        try {
            const session = await createSession()
            dispatch(openTab(session.id))
            dispatch(setActiveTab(session.id))
            dispatch(markSessionFresh(session.id))
            dispatch(setSessionLoading({ sessionId: session.id, isLoading: true }))
            dispatch(setSessionError({ sessionId: session.id, error: null }))
            dispatch(upsertSession({ ...session, nodes: [], commits: [], messages: [] } as any))
            handleActivate(session.id)
            dispatch(setSessionLoading({ sessionId: session.id, isLoading: false }))
        } catch (e: any) {
            dispatch(setSessionError({ sessionId: 'create', error: e?.message ?? 'Failed to create session' }))
        }
    }

    const handleDuplicate = useCallback(async (sourceSessionId: string) => {
        if (!user) {
            window.dispatchEvent(new CustomEvent('show:login', { detail: { returnUrl: window.location.pathname } }))
            return
        }

        // Optimistic: create a placeholder tab right next to the source
        const placeholderId = `dup-${Date.now()}`
        const sourceSession = sessionsById[sourceSessionId]
        const placeholderTitle = `${sourceSession?.title || 'Untitled'} (Copy)`
        dispatch(openTabAfter({ sessionId: placeholderId, afterSessionId: sourceSessionId }))
        dispatch(setSessionLoading({ sessionId: placeholderId, isLoading: true }))
        dispatch(setSessionLoadingMessage({ sessionId: placeholderId, message: t('duplicatingSession') }))
        dispatch(setSessionError({ sessionId: placeholderId, error: null }))
        dispatch(upsertSession({
            id: placeholderId, title: placeholderTitle,
            nodes: [], commits: [], messages: [],
            created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
            privacy: 'private', shared_with: [],
        } as any))

        try {
            const newSession = await duplicateSession(sourceSessionId)

            // Swap placeholder tab for the real session
            dispatch(closeTab(placeholderId))
            dispatch(openTabAfter({ sessionId: newSession.id, afterSessionId: sourceSessionId }))
            dispatch(setActiveTab(newSession.id))
            dispatch(setSessionLoading({ sessionId: newSession.id, isLoading: true }))
            dispatch(setSessionError({ sessionId: newSession.id, error: null }))
            dispatch(upsertSession({ ...newSession, nodes: [], commits: [], messages: [] } as any))
            try {
                window.history.pushState({}, '', `/session/${newSession.id}`)
                window.dispatchEvent(new PopStateEvent('popstate'))
            } catch { }

            // Hydrate full session data
            try {
                const info = await getSessionInfo(newSession.id)
                dispatch(upsertSession(info))
                const commits = Array.isArray((info as any).commits) ? (info as any).commits : []
                const nodes = Array.isArray((info as any).nodes) ? (info as any).nodes : []
                let lastNodeId: string | null = null
                if (commits.length > 0) {
                    const lastCommit = [...commits].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()).at(-1)
                    lastNodeId = lastCommit?.node_to_id ?? null
                }
                if (!lastNodeId && nodes.length > 0) {
                    const lastNode = [...nodes].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()).at(-1)
                    lastNodeId = lastNode?.id ?? null
                }
                if (lastNodeId) dispatch(setPendingInitialSelection({ sessionId: newSession.id, nodeId: lastNodeId }))
            } catch { }
            dispatch(setSessionLoading({ sessionId: newSession.id, isLoading: false }))
        } catch (e: any) {
            // Remove placeholder on failure
            dispatch(closeTab(placeholderId))
            console.error('Failed to duplicate session', e)
        }
    }, [dispatch, user, sessionsById])

    const handleTabContextMenu = useCallback((e: React.MouseEvent, sessionId: string) => {
        e.preventDefault()
        e.stopPropagation()
        setCtxMenu({ open: true, anchor: { x: e.clientX, y: e.clientY }, sessionId })
    }, [])

    const closeCtxMenu = useCallback(() => {
        setCtxMenu({ open: false, anchor: null, sessionId: null })
    }, [])

    const ctxMenuItems: ContextMenuItem[] = ctxMenu.sessionId ? [
        {
            id: 'duplicate',
            label: t('duplicateSession'),
            icon: <Copy size={14} />,
            onClick: () => { if (ctxMenu.sessionId) void handleDuplicate(ctxMenu.sessionId) },
        },
    ] : []

    const handleTitleSave = async (sessionId: string, newTitle: string) => {
        if (!sessionId) return
        dispatch(updateSessionTitle({ sessionId, title: newTitle }))
        try {
            await updateSession(sessionId, { title: newTitle })
        } catch (error) {
            console.error('Failed to update session title', error)
        }
    }

    return (
        <div className={styles.root} aria-label="Session tabs">
            <ContextMenu
                open={ctxMenu.open}
                anchor={ctxMenu.anchor}
                onClose={closeCtxMenu}
                items={ctxMenuItems}
            />
            <div className={styles.strip} role="tablist" aria-label="Open sessions">
                {openSessionIds.map((id) => {
                    const isActive = id === activeSessionId
                    const session = sessionsById[id]
                    const title = (session?.title || '').trim() || `Session ${id.slice(0, 6)}…`
                    const canEdit = Boolean(
                        session &&
                        user &&
                        (session.user_id === user.id || (session.shared_with || []).includes(user.id))
                    )
                    const unread = unreadById[id] ?? 0
                    const isProcessing = Boolean(chatBySessionId[id]?.isProcessing)

                    return (
                        <div
                            key={id}
                            className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
                            role="tab"
                            aria-selected={isActive}
                            tabIndex={isActive ? 0 : -1}
                            onClick={() => handleActivate(id)}
                            onContextMenu={(e) => handleTabContextMenu(e, id)}
                        >
                            {isActive ? (
                                <EditableTitle
                                    title={session?.title || ''}
                                    placeholder={`Session ${id.slice(0, 6)}...`}
                                    onSave={(next) => void handleTitleSave(id, next)}
                                    readOnly={!canEdit}
                                />
                            ) : (
                                <span className={styles.title} title={title}>{title}</span>
                            )}
                            <span className={styles.meta}>
                                {isProcessing && <span className={`${styles.dot} ${styles.dotProcessing}`} aria-label="Processing" />}
                                {!isProcessing && unread > 0 && <span className={styles.badge} aria-label={`${unread} unread`}>{unread}</span>}
                                <button
                                    type="button"
                                    className={styles.close}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        handleClose(id)
                                    }}
                                    aria-label="Close tab"
                                    title="Close tab"
                                >
                                    <X aria-hidden="true" />
                                </button>
                            </span>
                        </div>
                    )
                })}
                <IconButton
                    onClick={() => void handleNewTab()}
                    title="New session tab"
                    aria-label="New session tab"
                    className={styles.addButton}
                >
                    <Plus size={18} aria-hidden="true" />
                </IconButton>
            </div>
        </div>
    )
}

